/**
 * @file
 * @fileOverview This file contains the AframeObjectBuilder class for the Aframe scene.
 * @version 1.0.0
 * @module AFrameObjectBuilder
 * @requires three
 * @requires aframe
 * @author Parker Eli Bidigare
 * @copyright 2025
 * @date 2024-05-26
 */


import { SimplexNoise } from 'three/addons/math/SimplexNoise.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { TAARenderPass } from 'three/examples/jsm/postprocessing/TAARenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass.js';

import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';


/* @typedef {Object} Position
* @property {number} x - The x coordinate
* @property {number} y - The y coordinate
* @property {number} z - The z coordinate
*/

/**
* @typedef {Object} Rotation
* @property {number} x - Rotation around the x-axis in degrees
* @property {number} y - Rotation around the y-axis in degrees
* @property {number} z - Rotation around the z-axis in degrees
*/

/**
* @typedef {Object} Scale
* @property {number} x - Scale factor along the x-axis
* @property {number} y - Scale factor along the y-axis
* @property {number} z - Scale factor along the z-axis
*/

/**
* @typedef {'low'|'medium'|'high'|'ultra'} QualityLevel
*/

/**
* @typedef {Object} ShaderDefinition
* @property {string} vertexShader - The vertex shader code
* @property {string} fragmentShader - The fragment shader code
*/

/**
* @typedef {Object} ParticleSystemOptions
* @property {number} count - Number of particles
* @property {number} size - Size of each particle
* @property {string} color - Color of particles
* @property {number} speed - Movement speed
* @property {number} lifetime - Particle lifetime in seconds
* @property {number} spread - Spread distance from origin
*/

/**
* @typedef {Object} PortalOptions
* @property {Position} destination - Destination coordinates
* @property {Function} [onTrigger] - Callback when portal is triggered
* @property {string} [color] - Portal color
* @property {number} [transitionDuration] - Duration of transition effect
*/


const portalShaders = {
    // Enhanced particle shader
    particleShader: {
        vertexShader: `
            attribute float size;
            attribute vec3 customColor;
            attribute float phase;
            attribute float opacity;
            
            varying vec3 vColor;
            varying float vOpacity;
            varying vec2 vUv;
            varying float vPhase;
            
            void main() {
                vColor = customColor;
                vOpacity = opacity;
                vPhase = phase;
                vUv = uv;
                
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                float distanceFromCenter = length(position.xy);
                
                // Enhanced size attenuation
                float sizeAttenuation = size * (300.0 / -mvPosition.z);
                sizeAttenuation *= (1.0 + 0.2 * sin(phase + time));
                
                gl_PointSize = sizeAttenuation;
                gl_Position = projectionMatrix * mvPosition;
            }
        `,
        fragmentShader: `
            varying vec3 vColor;
            varying float vOpacity;
            varying vec2 vUv;
            varying float vPhase;
            uniform float time;
            
            void main() {
                vec2 center = vec2(0.5, 0.5);
                float dist = length(gl_PointCoord - center);
                
                // Complex particle shape
                float alpha = (1.0 - smoothstep(0.0, 0.5, dist)) * vOpacity;
                
                // Energy pulse effect
                float pulse = sin(vPhase + time * 2.0) * 0.5 + 0.5;
                
                // Inner glow
                float innerGlow = (1.0 - dist * 2.0) * 0.5;
                
                // Energy ripples
                float ripple = sin(dist * 20.0 - time * 3.0) * 0.1;
                
                // Combine effects
                vec3 finalColor = vColor + vec3(innerGlow) + vec3(ripple) * pulse;
                
                gl_FragColor = vec4(finalColor, alpha);
            }
        `
    },

    // Energy wave shader
    energyWaveShader: {
        vertexShader: `
            varying vec2 vUv;
            varying float vElevation;
            uniform float time;
            
            void main() {
                vUv = uv;
                
                // Create wave pattern
                float elevation = sin(position.x * 5.0 + time) * 
                                cos(position.y * 5.0 + time) * 0.1;
                                
                vElevation = elevation;
                vec3 pos = position + normal * elevation;
                
                gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
            }
        `,
        fragmentShader: `
            varying vec2 vUv;
            varying float vElevation;
            uniform vec3 color;
            uniform float time;
            
            void main() {
                float intensity = 1.0 - pow(vElevation * 10.0, 2.0);
                vec3 finalColor = mix(color, vec3(1.0), intensity * 0.5);
                
                // Add ripple effect
                float ripple = sin(vUv.x * 20.0 + vUv.y * 20.0 - time * 2.0) * 0.5 + 0.5;
                finalColor += ripple * 0.1;
                
                gl_FragColor = vec4(finalColor, intensity * 0.7);
            }
        `
    },

    // Distortion shader
    distortionShader: {
        vertexShader: `
            varying vec2 vUv;
            
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            varying vec2 vUv;
            uniform float time;
            uniform sampler2D tDiffuse;
            
            void main() {
                vec2 center = vec2(0.5, 0.5);
                vec2 coord = vUv;
                
                // Distortion effect
                float dist = length(coord - center);
                float angle = atan(coord.y - center.y, coord.x - center.x);
                
                // Spiral distortion
                float spiral = sin(dist * 20.0 - time * 2.0) * 0.1;
                coord += vec2(cos(angle), sin(angle)) * spiral * dist;
                
                // Pulse effect
                float pulse = sin(time * 3.0) * 0.02;
                coord += (coord - center) * pulse;
                
                vec4 texel = texture2D(tDiffuse, coord);
                gl_FragColor = texel;
            }
        `
    }
};


class AFrameObjectBuilder {

    /**
     * Creates an instance of AFrameObjectBuilder.
     * @constructor
     * @param {string} sceneId - ID of the A-Frame scene element
     * @throws {Error} When scene element is not found
     */
    constructor(sceneId) {
        this.scene = document.getElementById(sceneId);
        this.elements = new Map();
        this.shaders = new Map();
        this.counter = 0;
        
        let assets = this.scene.querySelector('a-assets');
        if (!assets) {
            assets = document.createElement('a-assets');
            this.scene.appendChild(assets);
        }
        this.assets = assets;
        this.initializeDefaultShaders();
    }

    /**
     * Generates a unique identifier for scene elements.
     * @private
     * @param {string} prefix - Prefix for the generated ID
     * @returns {string} Unique identifier
     */
    generateId(prefix) {
        return `${prefix}_${this.counter++}`;
    }

    /**
     * Retrieves an element by its ID.
     * @param {string} id - Element identifier
     * @returns {Element|undefined} The found element or undefined
     */
    getElement(id) {
        return this.elements.get(id);
    }

    /**
     * Creates a box primitive.
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {number} [width=1] - Box width
     * @param {number} [height=1] - Box height
     * @param {number} [depth=1] - Box depth
     * @param {string} [color='red'] - Box color
     * @returns {string} ID of created box
     */
    addBox(x = 0, y = 0, z = 0, width = 1, height = 1, depth = 1, color = 'red') {
        const id = this.generateId('box');
        const box = document.createElement('a-box');
        box.setAttribute('position', `${x} ${y} ${z}`);
        box.setAttribute('width', width);
        box.setAttribute('height', height);
        box.setAttribute('depth', depth);
        box.setAttribute('color', color);
        box.id = id;
        this.scene.appendChild(box);
        this.elements.set(id, box);
        return id;
    }

    /**
     * Creates a sphere primitive.
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {number} [radius=1] - Sphere radius
     * @param {string} [color='red'] - Sphere color
     * @returns {string} ID of created sphere
     */
    addSphere(x = 0, y = 0, z = 0, radius = 1, color = 'red') {
        const id = this.generateId('sphere');
        const sphere = document.createElement('a-sphere');
        sphere.setAttribute('position', `${x} ${y} ${z}`);
        sphere.setAttribute('radius', radius);
        sphere.setAttribute('color', color);
        sphere.id = id;
        this.scene.appendChild(sphere);
        this.elements.set(id, sphere);
        return id;
    }

    /**
     * Creates a cylinder primitive.
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {number} [radius=0.5] - Cylinder radius
     * @param {number} [height=1] - Cylinder height
     * @param {string} [color='red'] - Cylinder color
     * @returns {string} ID of created cylinder
     */
    addCylinder(x = 0, y = 0, z = 0, radius = 0.5, height = 1, color = 'red') {
        const id = this.generateId('cylinder');
        const cylinder = document.createElement('a-cylinder');
        cylinder.setAttribute('position', `${x} ${y} ${z}`);
        cylinder.setAttribute('radius', radius);
        cylinder.setAttribute('height', height);
        cylinder.setAttribute('color', color);
        cylinder.id = id;
        this.scene.appendChild(cylinder);
        this.elements.set(id, cylinder);
        return id;
    }

    /**
     * Creates a plane primitive.
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {number} [width=1] - Plane width
     * @param {number} [height=1] - Plane height
     * @param {string} [color='red'] - Plane color
     * @returns {string} ID of created plane
     */
    addPlane(x = 0, y = 0, z = 0, width = 1, height = 1, color = 'red') {
        const id = this.generateId('plane');
        const plane = document.createElement('a-plane');
        plane.setAttribute('position', `${x} ${y} ${z}`);
        plane.setAttribute('width', width);
        plane.setAttribute('height', height);
        plane.setAttribute('color', color);
        plane.id = id;
        this.scene.appendChild(plane);
        this.elements.set(id, plane);
        return id;
    }

    /**
     * Creates a cone primitive.
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {number} [radius=0.5] - Base radius
     * @param {number} [height=1] - Cone height
     * @param {string} [color='red'] - Cone color
     * @returns {string} ID of created cone
     */
    addCone(x = 0, y = 0, z = 0, radius = 0.5, height = 1, color = 'red') {
        const id = this.generateId('cone');
        const cone = document.createElement('a-cone');
        cone.setAttribute('position', `${x} ${y} ${z}`);
        cone.setAttribute('radius-bottom', radius);
        cone.setAttribute('height', height);
        cone.setAttribute('color', color);
        cone.id = id;
        this.scene.appendChild(cone);
        this.elements.set(id, cone);
        return id;
    }

    /**
     * Creates a torus primitive.
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {number} [radius=1] - Torus radius
     * @param {number} [radiusTubular=0.1] - Tubular radius
     * @param {string} [color='red'] - Torus color
     * @returns {string} ID of created torus
     */
    addTorus(x = 0, y = 0, z = 0, radius = 1, radiusTubular = 0.1, color = 'red') {
        const id = this.generateId('torus');
        const torus = document.createElement('a-torus');
        torus.setAttribute('position', `${x} ${y} ${z}`);
        torus.setAttribute('radius', radius);
        torus.setAttribute('radius-tubular', radiusTubular);
        torus.setAttribute('color', color);
        torus.id = id;
        this.scene.appendChild(torus);
        this.elements.set(id, torus);
        return id;
    }

    /**
     * Creates a text entity.
     * @param {string} text - Text content
     * @param {number} [x=0] - X position
     * @param {number} [y=0] - Y position
     * @param {number} [z=0] - Z position
     * @param {string} [color='white'] - Text color
     * @param {number} [width=10] - Text width
     * @param {'left'|'center'|'right'} [align='center'] - Text alignment
     * @param {'top'|'center'|'bottom'} [baseline='center'] - Text baseline
     * @returns {string} ID of created text entity
     */
    addText(text, x = 0, y = 0, z = 0, color = 'white', width = 10, align = 'center', baseline = 'center') {
        const id = this.generateId('text');
        const textElement = document.createElement('a-text');
        textElement.setAttribute('position', `${x} ${y} ${z}`);
        textElement.setAttribute('value', text);
        textElement.setAttribute('color', color);
        textElement.setAttribute('width', width);
        textElement.setAttribute('align', align);
        textElement.setAttribute('baseline', baseline);
        textElement.id = id;
        this.scene.appendChild(textElement);
        this.elements.set(id, textElement);
        return id;
    }

    /**
     * Adds an image asset to the scene.
     * @async
     * @param {string} src - Image source URL
     * @param {string} id - Asset ID
     * @returns {Promise<string>} ID of created image asset
     * @throws {Error} When image loading fails
     */
    addImage(src, id) {
        const asset = document.createElement('img');
        asset.setAttribute('id', id);
        asset.setAttribute('src', src);
        return new Promise((resolve, reject) => {
            asset.addEventListener('loaded', () => resolve(id));
            asset.addEventListener('error', reject);
            this.assets.appendChild(asset);
        });
    }

    /**
     * Adds a video asset to the scene.
     * @async
     * @param {string} src - Video source URL
     * @param {string} id - Asset ID
     * @returns {Promise<string>} ID of created video asset
     * @throws {Error} When video loading fails
     */
    addVideo(src, id) {
        const asset = document.createElement('video');
        asset.setAttribute('id', id);
        asset.setAttribute('src', src);
        return new Promise((resolve, reject) => {
            asset.addEventListener('loaded', () => resolve(id));
            asset.addEventListener('error', reject);
            this.assets.appendChild(asset);
        });
    }

    /**
     * Creates a custom geometry from points.
     * @param {number[][]} points - Array of 3D points
     * @returns {string} ID of created model
     */
    addModelFromPoints(points) {
        const id = this.generateId('custom-geometry');
        const entity = document.createElement('a-entity');
        const geometry = new THREE.BufferGeometry();
        const vertices = new Float32Array(points.flat());
        geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        const material = new THREE.MeshStandardMaterial();
        const mesh = new THREE.Mesh(geometry, material);
        entity.setObject3D('mesh', mesh);
        entity.id = id;
        this.scene.appendChild(entity);
        this.elements.set(id, entity);
        return id;
    }

    /**
     * Updates entity position.
     * @param {string} id - Entity ID
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {number} z - Z coordinate
     * @returns {AFrameObjectBuilder} this
     */
    setPosition(id, x, y, z) {
        const element = this.getElement(id);
        element.setAttribute('position', `${x} ${y} ${z}`);
        return this;
    }

    /**
     * Gets entity position.
     * @param {string} id - Entity ID
     * @returns {Position} Current position
     */
    getPosition(id) {
        const element = this.getElement(id);
        const pos = element.getAttribute('position');
        return {
            x: parseFloat(pos.x),
            y: parseFloat(pos.y),
            z: parseFloat(pos.z)
        };
    }

    /**
     * Sets entity rotation.
     * @param {string} id - Entity ID
     * @param {number} x - X rotation
     * @param {number} y - Y rotation
     * @param {number} z - Z rotation
     * @returns {AFrameObjectBuilder} this
     */
    setRotation(id, x, y, z) {
        const element = this.getElement(id);
        element.setAttribute('rotation', `${x} ${y} ${z}`);
        return this;
    }

    /**
     * Gets entity rotation.
     * @param {string} id - Entity ID
     * @returns {Rotation} Current rotation
     */
    getRotation(id) {
        const element = this.getElement(id);
        const rot = element.getAttribute('rotation');
        return {
            x: parseFloat(rot.x),
            y: parseFloat(rot.y),
            z: parseFloat(rot.z)
        };
    }

    /**
     * Sets entity scale.
     * @param {string} id - Entity ID
     * @param {number} x - X scale
     * @param {number} y - Y scale
     * @param {number} z - Z scale
     * @returns {AFrameObjectBuilder} this
     */
    setScale(id, x, y, z) {
        const element = this.getElement(id);
        element.setAttribute('scale', `${x} ${y} ${z}`);
        return this;
    }

    /**
     * Gets entity scale.
     * @param {string} id - Entity ID
     * @returns {Scale} Current scale
     */
    getScale(id) {
        const element = this.getElement(id);
        const scale = element.getAttribute('scale');
        return {
            x: parseFloat(scale.x),
            y: parseFloat(scale.y),
            z: parseFloat(scale.z)
        };
    }

    /**
     * Sets entity texture.
     * @param {string} id - Entity ID
     * @param {string} textureUrl - Texture URL
     * @param {string} [repeat='1 1'] - Texture repeat
     * @returns {AFrameObjectBuilder} this
     */
    setTexture(id, textureUrl, repeat = '1 1') {
        const element = this.getElement(id);
        element.setAttribute('material', {
            src: textureUrl,
            repeat: repeat
        });
        return this;
    }

    initializeDefaultShaders() {
        this.registerShader('gradient', {
            colorTop: { type: 'color', default: '#1a237e' },
            colorBottom: { type: 'color', default: '#311b92' }
        }, `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `, `
            uniform vec3 colorTop;
            uniform vec3 colorBottom;
            varying vec2 vUv;
            void main() {
                gl_FragColor = vec4(mix(colorBottom, colorTop, vUv.y), 1.0);
            }
        `);

        this.registerShader('waves', {
            color: { type: 'color', default: '#7e57c2' },
            timeMsec: { type: 'time', default: 0 }
        }, `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `, `
            uniform float timeMsec;
            uniform vec3 color;
            varying vec2 vUv;
            void main() {
                float time = timeMsec / 1000.0;
                float wave = sin(vUv.x * 10.0 + time) * 0.5 + 0.5;
                gl_FragColor = vec4(color * wave, 1.0);
            }
        `);
    }

    registerShader(name, schema, vertexShader, fragmentShader) {
        if (typeof AFRAME === 'undefined') {
            console.error('A-Frame not found!');
            return;
        }

        AFRAME.registerShader(name, {
            schema: schema,
            vertexShader: vertexShader,
            fragmentShader: fragmentShader
        });

        this.shaders.set(name, { schema, vertexShader, fragmentShader });
    }

    applyShader(id, shaderName, ...uniforms) {
        const element = this.getElement(id);
        const shader = this.shaders.get(shaderName);
        if (!shader) return this;

        const uniformValues = {};
        Object.keys(shader.schema).forEach((key, index) => {
            if (uniforms[index] !== undefined) {
                uniformValues[key] = uniforms[index];
            }
        });

        element.setAttribute('material', {
            shader: shaderName,
            ...uniformValues
        });
        return this;
    }

    addAnimation(id, property, from, to, dur = 1000, loop = false, easing = 'linear') {
        const element = this.getElement(id);
        const animation = document.createElement('a-animation');
        animation.setAttribute('attribute', property);
        animation.setAttribute('from', from);
        animation.setAttribute('to', to);
        animation.setAttribute('dur', dur);
        animation.setAttribute('easing', easing);
        animation.setAttribute('loop', loop);
        element.appendChild(animation);
        return this;
    }

    generateParticleTexture(size, type = 'soft', color = '#ffffff') {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const center = size / 2;
        
        switch(type) {
            case 'fire':
                const fireGradient = ctx.createRadialGradient(
                    center, center * 1.2, 0,
                    center, center, size/2
                );
                fireGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
                fireGradient.addColorStop(0.3, 'rgba(255, 200, 100, 0.8)');
                fireGradient.addColorStop(0.7, 'rgba(255, 100, 50, 0.4)');
                fireGradient.addColorStop(1, 'rgba(255, 50, 0, 0)');
                
                ctx.fillStyle = fireGradient;
                ctx.fillRect(0, 0, size, size);
                
                ctx.globalCompositeOperation = 'overlay';
                const noiseSize = size / 8;
                for (let i = 0; i < size; i += noiseSize) {
                    for (let j = 0; j < size; j += noiseSize) {
                        ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.2})`;
                        ctx.fillRect(i, j, noiseSize, noiseSize);
                    }
                }
                break;

            case 'spark':
                ctx.fillStyle = 'rgba(0, 0, 0, 0)';
                ctx.fillRect(0, 0, size, size);
                
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
                ctx.lineWidth = size / 16;
                
                for (let i = 0; i < 4; i++) {
                    const angle = (Math.PI / 4) * i;
                    ctx.beginPath();
                    ctx.moveTo(center, center);
                    ctx.lineTo(
                        center + Math.cos(angle) * size/2,
                        center + Math.sin(angle) * size/2
                    );
                    ctx.stroke();
                }
                
                const sparkGlow = ctx.createRadialGradient(
                    center, center, 0,
                    center, center, size/4
                );
                sparkGlow.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
                sparkGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
                
                ctx.fillStyle = sparkGlow;
                ctx.fillRect(0, 0, size, size);
                break;

            case 'smoke':
                const smokeGradient = ctx.createRadialGradient(
                    center, center, 0,
                    center, center, size/2
                );
                smokeGradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
                smokeGradient.addColorStop(0.5, 'rgba(128, 128, 128, 0.2)');
                smokeGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                
                ctx.fillStyle = smokeGradient;
                ctx.fillRect(0, 0, size, size);
                
                ctx.globalCompositeOperation = 'overlay';
                for (let i = 0; i < 3; i++) {
                    const radius = size * (0.3 + Math.random() * 0.2);
                    const x = center + (Math.random() - 0.5) * size * 0.3;
                    const y = center + (Math.random() - 0.5) * size * 0.3;
                    
                    const cloudGradient = ctx.createRadialGradient(
                        x, y, 0,
                        x, y, radius
                    );
                    cloudGradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
                    cloudGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                    
                    ctx.fillStyle = cloudGradient;
                    ctx.beginPath();
                    ctx.arc(x, y, radius, 0, Math.PI * 2);
                    ctx.fill();
                }
                break;

            case 'snow':
                ctx.fillStyle = 'rgba(0, 0, 0, 0)';
                ctx.fillRect(0, 0, size, size);
                
                const flakeSize = size * 0.8;
                for (let i = 0; i < 6; i++) {
                    const angle = (Math.PI / 3) * i;
                    
                    ctx.save();
                    ctx.translate(center, center);
                    ctx.rotate(angle);
                    
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.lineTo(flakeSize/2, 0);
                    
                    ctx.lineWidth = size / 8;
                    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
                    ctx.lineCap = 'round';
                    ctx.stroke();
                    
                    ctx.restore();
                }
                
                const snowGlow = ctx.createRadialGradient(
                    center, center, 0,
                    center, center, size/4
                );
                snowGlow.addColorStop(0, 'rgba(255, 255, 255, 0.5)');
                snowGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
                
                ctx.globalCompositeOperation = 'overlay';
                ctx.fillStyle = snowGlow;
                ctx.fillRect(0, 0, size, size);
                break;

            case 'rain':
                ctx.fillStyle = 'rgba(0, 0, 0, 0)';
                ctx.fillRect(0, 0, size, size);
                
                ctx.strokeStyle = 'rgba(200, 200, 255, 0.8)';
                ctx.lineWidth = size / 16;
                ctx.lineCap = 'round';
                
                ctx.beginPath();
                ctx.moveTo(center, size * 0.2);
                ctx.lineTo(center, size * 0.8);
                ctx.stroke();
                
                const rainGlow = ctx.createLinearGradient(
                    center, 0,
                    center, size
                );
                rainGlow.addColorStop(0, 'rgba(200, 200, 255, 0.2)');
                rainGlow.addColorStop(0.5, 'rgba(200, 200, 255, 0.4)');
                rainGlow.addColorStop(1, 'rgba(200, 200, 255, 0.2)');
                
                ctx.strokeStyle = rainGlow;
                ctx.lineWidth = size / 8;
                ctx.stroke();
                break;

            case 'magic':
                ctx.fillStyle = 'rgba(0, 0, 0, 0)';
                ctx.fillRect(0, 0, size, size);
                
                const magicGlow = ctx.createRadialGradient(
                    center, center, 0,
                    center, center, size/2
                );
                magicGlow.addColorStop(0, 'rgba(255, 255, 255, 1)');
                magicGlow.addColorStop(0.2, 'rgba(255, 200, 255, 0.8)');
                magicGlow.addColorStop(0.5, 'rgba(200, 100, 255, 0.4)');
                magicGlow.addColorStop(1, 'rgba(100, 0, 255, 0)');
                
                ctx.fillStyle = magicGlow;
                ctx.fillRect(0, 0, size, size);
                
                for (let i = 0; i < 5; i++) {
                    const angle = (Math.PI * 2 / 5) * i;
                    const radius = size * 0.4;
                    
                    ctx.beginPath();
                    ctx.moveTo(
                        center + Math.cos(angle) * radius * 0.5,
                        center + Math.sin(angle) * radius * 0.5
                    );
                    ctx.lineTo(
                        center + Math.cos(angle) * radius,
                        center + Math.sin(angle) * radius
                    );
                    
                    ctx.lineWidth = size / 16;
                    ctx.strokeStyle = 'rgba(255, 200, 255, 0.6)';
                    ctx.stroke();
                }
                break;

            default: // soft, default particle
                const defaultGradient = ctx.createRadialGradient(
                    center, center, 0,
                    center, center, size/2
                );
                defaultGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
                defaultGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.8)');
                defaultGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.3)');
                defaultGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
                
                ctx.fillStyle = defaultGradient;
                ctx.fillRect(0, 0, size, size);
        }

        const texture = new THREE.Texture(canvas);
        texture.needsUpdate = true;
        return texture;
    }


    addParticleSystem(count = 1000, size = 0.1, color = '#ffffff', speed = 1, lifetime = 1, spread = 10, shape = 'sphere', emitterType = 'point', quality = 'medium', textureType = 'soft') {
        const qualitySettings = {
            low: {
                multiplier: 0.5,
                sizeVariation: 0.2,
                alphaTest: 0.3,
                textureSize: 32,
                turbulence: 0.5
            },
            medium: {
                multiplier: 1,
                sizeVariation: 0.3,
                alphaTest: 0.2,
                textureSize: 64,
                turbulence: 1
            },
            high: {
                multiplier: 2,
                sizeVariation: 0.4,
                alphaTest: 0.1,
                textureSize: 128,
                turbulence: 1.5
            },
            ultra: {
                multiplier: 4,
                sizeVariation: 0.5,
                alphaTest: 0.05,
                textureSize: 256,
                turbulence: 2
            }
        };

        const settings = qualitySettings[quality] || qualitySettings.medium;
        const actualCount = Math.floor(count * settings.multiplier);
        
        const id = this.generateId('particles');
        const entity = document.createElement('a-entity');
        const particles = new THREE.BufferGeometry();
        
        const positions = new Float32Array(actualCount * 3);
        const velocities = new Float32Array(actualCount * 3);
        const accelerations = new Float32Array(actualCount * 3);
        const startTimes = new Float32Array(actualCount);
        const sizes = new Float32Array(actualCount);
        const rotations = new Float32Array(actualCount);
        const rotationSpeeds = new Float32Array(actualCount);
        const colors = new Float32Array(actualCount * 3);
        
        const colorObj = new THREE.Color(color);
        const shapeGenerators = {
            sphere: () => {
                const theta = Math.random() * Math.PI * 2;
                const phi = Math.acos((Math.random() * 2) - 1);
                const r = Math.pow(Math.random(), 1/3) * spread;
                return [
                    r * Math.sin(phi) * Math.cos(theta),
                    r * Math.sin(phi) * Math.sin(theta),
                    r * Math.cos(phi)
                ];
            },
            cube: () => [
                (Math.random() - 0.5) * spread,
                (Math.random() - 0.5) * spread,
                (Math.random() - 0.5) * spread
            ],
            cone: () => {
                const radius = Math.random() * spread;
                const theta = Math.random() * Math.PI * 2;
                const y = Math.random() * spread;
                return [
                    radius * Math.cos(theta),
                    y,
                    radius * Math.sin(theta)
                ];
            },
            disc: () => {
                const radius = Math.sqrt(Math.random()) * spread;
                const theta = Math.random() * Math.PI * 2;
                return [
                    radius * Math.cos(theta),
                    0,
                    radius * Math.sin(theta)
                ];
            }
        };

        for (let i = 0; i < actualCount; i++) {
            const i3 = i * 3;
            
            const pos = shapeGenerators[shape]?.() || shapeGenerators.sphere();
            if (emitterType === 'point') {
                positions[i3] = 0;
                positions[i3 + 1] = 0;
                positions[i3 + 2] = 0;
            } else {
                positions[i3] = pos[0];
                positions[i3 + 1] = pos[1];
                positions[i3 + 2] = pos[2];
            }
            
            const angle = Math.random() * Math.PI * 2;
            const upwardBias = shape === 'cone' ? 0.5 : 0;
            
            velocities[i3] = Math.cos(angle) * speed * (1 + (Math.random() - 0.5) * 0.5);
            velocities[i3 + 1] = (Math.sin(angle) * speed + upwardBias * speed) * (1 + (Math.random() - 0.5) * 0.5);
            velocities[i3 + 2] = (Math.random() - 0.5) * speed;
            
            accelerations[i3] = (Math.random() - 0.5) * 0.1;
            accelerations[i3 + 1] = (Math.random() - 0.5) * 0.1 - 0.1;
            accelerations[i3 + 2] = (Math.random() - 0.5) * 0.1;
            
            startTimes[i] = Math.random() * lifetime;
            sizes[i] = size * (1 + (Math.random() - 0.5) * settings.sizeVariation);
            rotations[i] = Math.random() * Math.PI * 2;
            rotationSpeeds[i] = (Math.random() - 0.5) * 2;
            
            colors[i3] = colorObj.r;
            colors[i3 + 1] = colorObj.g;
            colors[i3 + 2] = colorObj.b;
        }
        
        particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        particles.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));
        particles.setAttribute('acceleration', new THREE.BufferAttribute(accelerations, 3));
        particles.setAttribute('startTime', new THREE.BufferAttribute(startTimes, 1));
        particles.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        particles.setAttribute('rotation', new THREE.BufferAttribute(rotations, 1));
        particles.setAttribute('rotationSpeed', new THREE.BufferAttribute(rotationSpeeds, 1));
        particles.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        
        const particleTexture = this.generateParticleTexture(settings.textureSize, textureType, color);
        
        const material = new THREE.PointsMaterial({
            size: size,
            map: particleTexture,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            alphaTest: settings.alphaTest,
            vertexColors: true
        });
        
        const particleSystem = new THREE.Points(particles, material);
        entity.setObject3D('particle-system', particleSystem);
        
        const clock = new THREE.Clock();
        const turbulence = new THREE.Vector3();
        
        const animate = () => {
            const positions = particles.attributes.position.array;
            const velocities = particles.attributes.velocity.array;
            const accelerations = particles.attributes.acceleration.array;
            const startTimes = particles.attributes.startTime.array;
            const rotations = particles.attributes.rotation.array;
            const rotationSpeeds = particles.attributes.rotationSpeed.array;
            
            const time = clock.getElapsedTime();
            const delta = clock.getDelta();
            
            turbulence.set(
                Math.sin(time * 0.7) * settings.turbulence,
                Math.cos(time * 0.3) * settings.turbulence,
                Math.sin(time * 0.2) * settings.turbulence
            );
            
            for (let i = 0; i < actualCount; i++) {
                const i3 = i * 3;
                
                let particleTime = (time - startTimes[i]) % lifetime;
                if (particleTime < 0) particleTime += lifetime;
                
                const lifeProgress = particleTime / lifetime;
                
                if (emitterType === 'point') {
                    positions[i3] = velocities[i3] * particleTime + 0.5 * accelerations[i3] * particleTime * particleTime;
                    positions[i3 + 1] = velocities[i3 + 1] * particleTime + 0.5 * accelerations[i3 + 1] * particleTime * particleTime;
                    positions[i3 + 2] = velocities[i3 + 2] * particleTime + 0.5 * accelerations[i3 + 2] * particleTime * particleTime;
                } else {
                    velocities[i3] += (accelerations[i3] + turbulence.x) * delta;
                    velocities[i3 + 1] += (accelerations[i3 + 1] + turbulence.y) * delta;
                    velocities[i3 + 2] += (accelerations[i3 + 2] + turbulence.z) * delta;
                    
                    positions[i3] += velocities[i3] * delta;
                    positions[i3 + 1] += velocities[i3 + 1] * delta;
                    positions[i3 + 2] += velocities[i3 + 2] * delta;
                }
                
                rotations[i] += rotationSpeeds[i] * delta;
                
                if (lifeProgress >= 1) {
                    if (emitterType === 'point') {
                        positions[i3] = 0;
                        positions[i3 + 1] = 0;
                        positions[i3 + 2] = 0;
                    } else {
                        const pos = shapeGenerators[shape]();
                        positions[i3] = pos[0];
                        positions[i3 + 1] = pos[1];
                        positions[i3 + 2] = pos[2];
                    }
                    startTimes[i] = time;
                }
                
                const opacity = Math.min(1, Math.min(lifeProgress * 2, (1 - lifeProgress) * 2));
                colors[i3 + 3] = opacity;
            }
            
            particles.attributes.position.needsUpdate = true;
            particles.attributes.rotation.needsUpdate = true;
            particles.attributes.color.needsUpdate = true;
            
            requestAnimationFrame(animate);
        };
        
        animate();
        
        entity.id = id;
        this.scene.appendChild(entity);
        this.elements.set(id, entity);
        return id;
    }

    addFireEffect(x = 0, y = 0, z = 0, intensity = 1, quality = 'medium') {
        const id = this.addParticleSystem(
            200 * intensity,
            0.3,
            '#ff4400',
            2 * intensity,
            1.5,
            1,
            'cone',
            'point',
            quality
        );
        const entity = this.getElement(id);
        entity.setAttribute('position', `${x} ${y} ${z}`);
        return id;
    }

    addSmokeEffect(x = 0, y = 0, z = 0, intensity = 1, quality = 'medium') {
        const id = this.addParticleSystem(
            100 * intensity,
            0.5,
            '#555555',
            1 * intensity,
            2,
            2,
            'cone',
            'point',
            quality
        );
        const entity = this.getElement(id);
        entity.setAttribute('position', `${x} ${y} ${z}`);
        return id;
    }

    addSparkleEffect(x = 0, y = 0, z = 0, intensity = 1, quality = 'medium') {
        const id = this.addParticleSystem(
            150 * intensity,
            0.15,
            '#ffff00',
            2 * intensity,
            2,
            3,
            'sphere',
            'volume',
            quality,
            'spark'
        );

        const entity = this.getElement(id);
        entity.setAttribute('position', `${x} ${y} ${z}`);

        const particleSystem = entity.object3D.children[0];
        const geometry = particleSystem.geometry;
        const count = geometry.attributes.position.count;

        const positions = geometry.attributes.position.array;
        const velocities = geometry.attributes.velocity.array;
        const colors = geometry.attributes.color.array;
        const sizes = geometry.attributes.size.array;

        // Store individual particle data
        const particles = [];
        for (let i = 0; i < count; i++) {
            particles.push({
                phase: Math.random() * Math.PI * 2,
                twinkleSpeed: 3 + Math.random() * 5,
                baseSize: 0.1 + Math.random() * 0.2,
                colorShift: Math.random(),
                lifetime: Math.random() * 2
            });
        }

        // Initialize particle properties
        for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            
            // Random position within sphere
            const phi = Math.random() * Math.PI * 2;
            const theta = Math.acos(2 * Math.random() - 1);
            const r = Math.cbrt(Math.random()) * 3;

            positions[i3] = r * Math.sin(theta) * Math.cos(phi);
            positions[i3 + 1] = r * Math.sin(theta) * Math.sin(phi);
            positions[i3 + 2] = r * Math.cos(theta);

            // Gentle outward drift
            velocities[i3] = (Math.random() - 0.5) * 0.5;
            velocities[i3 + 1] = (Math.random() - 0.5) * 0.5;
            velocities[i3 + 2] = (Math.random() - 0.5) * 0.5;

            // Initial colors (gold/yellow)
            colors[i3] = 1;      // R
            colors[i3 + 1] = 1;  // G
            colors[i3 + 2] = 0;  // B

            sizes[i] = particles[i].baseSize;
        }

        let startTime = Date.now();

        const customAnimate = () => {
            const currentTime = Date.now();
            const elapsed = (currentTime - startTime) * 0.001; // seconds

            for (let i = 0; i < count; i++) {
                const i3 = i * 3;
                const particle = particles[i];

                // Update positions with gentle movement
                positions[i3] += velocities[i3] * 0.016;
                positions[i3 + 1] += velocities[i3 + 1] * 0.016;
                positions[i3 + 2] += velocities[i3 + 2] * 0.016;

                // Add slight drift to velocities
                velocities[i3] += (Math.random() - 0.5) * 0.01;
                velocities[i3 + 1] += (Math.random() - 0.5) * 0.01;
                velocities[i3 + 2] += (Math.random() - 0.5) * 0.01;

                // Twinkling effect
                const twinkle = Math.sin(elapsed * particle.twinkleSpeed + particle.phase);
                const brightness = 0.5 + (twinkle + 1) * 0.25;

                // Color cycling
                const hue = (elapsed * 0.1 + particle.colorShift) % 1;
                const color = new THREE.Color().setHSL(hue, 0.7, brightness);
                
                colors[i3] = color.r;
                colors[i3 + 1] = color.g;
                colors[i3 + 2] = color.b;

                // Size pulsing
                sizes[i] = particle.baseSize * (1 + twinkle * 0.3);

                // Keep particles within bounds
                const distance = Math.sqrt(
                    positions[i3] * positions[i3] + 
                    positions[i3 + 1] * positions[i3 + 1] + 
                    positions[i3 + 2] * positions[i3 + 2]
                );

                if (distance > 3) {
                    const scale = 2.9 / distance;
                    positions[i3] *= scale;
                    positions[i3 + 1] *= scale;
                    positions[i3 + 2] *= scale;

                    velocities[i3] *= -0.5;
                    velocities[i3 + 1] *= -0.5;
                    velocities[i3 + 2] *= -0.5;
                }

                // Particle lifetime and respawn
                particle.lifetime -= 0.016;
                if (particle.lifetime <= 0) {
                    // Reset particle
                    particle.lifetime = Math.random() * 2;
                    particle.phase = Math.random() * Math.PI * 2;
                    particle.twinkleSpeed = 3 + Math.random() * 5;
                    
                    const phi = Math.random() * Math.PI * 2;
                    const theta = Math.acos(2 * Math.random() - 1);
                    const r = Math.cbrt(Math.random()) * 3;

                    positions[i3] = r * Math.sin(theta) * Math.cos(phi);
                    positions[i3 + 1] = r * Math.sin(theta) * Math.sin(phi);
                    positions[i3 + 2] = r * Math.cos(theta);

                    velocities[i3] = (Math.random() - 0.5) * 0.5;
                    velocities[i3 + 1] = (Math.random() - 0.5) * 0.5;
                    velocities[i3 + 2] = (Math.random() - 0.5) * 0.5;
                }
            }

            // Update buffers
            geometry.attributes.position.needsUpdate = true;
            geometry.attributes.color.needsUpdate = true;
            geometry.attributes.size.needsUpdate = true;

            requestAnimationFrame(customAnimate);
        };

        // Material setup
        particleSystem.material.vertexColors = true;
        particleSystem.material.transparent = true;
        particleSystem.material.depthWrite = false;
        particleSystem.material.blending = THREE.AdditiveBlending;

        customAnimate();
        
        return id;
    }

    addSnowEffect(x = 0, y = 0, z = 0, intensity = 1, quality = 'medium') {
        const id = this.addParticleSystem(
            300,                    // count
            0.15,                   // size 
            '#ffffff',              // color
            2,                      // speed
            8,                      // lifetime
            30,                     // spread
            'cube',                 // shape
            'volume',               // emitterType
            quality,                // quality
            'snow'                  // textureType
        );

        const entity = this.getElement(id);
        entity.setAttribute('position', `${x} ${y} ${z}`);

        const particleSystem = entity.object3D.children[0];
        const geometry = particleSystem.geometry;
        const count = geometry.attributes.position.count;

        const positions = geometry.attributes.position.array;
        const velocities = geometry.attributes.velocity.array;
        const accelerations = geometry.attributes.acceleration.array;
        const startTimes = geometry.attributes.startTime.array;
        const originalY = y;  // Store original Y position for respawning

        // Custom animation function to override default behavior
        const customAnimate = () => {
            const time = Date.now() * 0.001;  // Current time in seconds
            
            for (let i = 0; i < count; i++) {
                const i3 = i * 3;
                
                // Add drifting motion using sine waves
                const drift = Math.sin(time + startTimes[i]) * 0.3;
                velocities[i3] = drift;  // X drift
                
                // Update positions
                positions[i3] += velocities[i3] * 0.016;  // X position
                positions[i3 + 1] += velocities[i3 + 1] * 0.016;  // Y position (falling)
                positions[i3 + 2] += (Math.sin(time * 0.5 + startTimes[i]) * 0.2) * 0.016;  // Z drift
                
                // Respawn logic
                if (positions[i3 + 1] < originalY - 30) {  // If fallen too low
                    // Reset position to top with random X and Z
                    positions[i3] = (Math.random() - 0.5) * 30 + x;  // Random X
                    positions[i3 + 1] = originalY + Math.random() * 5;  // Top with slight variation
                    positions[i3 + 2] = (Math.random() - 0.5) * 30 + z;  // Random Z
                    
                    // Reset velocities
                    velocities[i3] = (Math.random() - 0.5) * 0.5;  // Random X drift
                    velocities[i3 + 1] = -2 - Math.random();  // Downward speed
                    velocities[i3 + 2] = (Math.random() - 0.5) * 0.5;  // Random Z drift
                    
                    // New random start time for varied motion
                    startTimes[i] = Math.random() * Math.PI * 2;
                }
            }

            // Update geometry
            geometry.attributes.position.needsUpdate = true;
            geometry.attributes.velocity.needsUpdate = true;
            
            requestAnimationFrame(customAnimate);
        };

        // Initial setup
        for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            
            // Initial positions
            positions[i3] = (Math.random() - 0.5) * 30 + x;
            positions[i3 + 1] = originalY + Math.random() * 30;
            positions[i3 + 2] = (Math.random() - 0.5) * 30 + z;
            
            // Initial velocities
            velocities[i3] = (Math.random() - 0.5) * 0.5;
            velocities[i3 + 1] = -2 - Math.random();
            velocities[i3 + 2] = (Math.random() - 0.5) * 0.5;
            
            // Random start times for varied motion
            startTimes[i] = Math.random() * Math.PI * 2;
        }

        // Override material settings
        particleSystem.material.size = 0.15;
        particleSystem.material.opacity = 0.8;
        particleSystem.material.transparent = true;
        particleSystem.material.depthWrite = false;
        particleSystem.material.blending = THREE.AdditiveBlending;

        // Start animation
        customAnimate();

        // Update all modified attributes
        geometry.attributes.position.needsUpdate = true;
        geometry.attributes.velocity.needsUpdate = true;
        geometry.attributes.startTime.needsUpdate = true;

        return id;
    }

    addRainEffect(x = 0, y = 0, z = 0, intensity = 1, quality = 'medium') {
        const id = this.addParticleSystem(
            500 * intensity,        // count
            0.1,                    // size
            '#aaaaff',              // color
            15 * intensity,         // speed (increased for faster rain)
            1,                      // lifetime
            30,                     // spread
            'cube',                 // shape
            'volume',               // emitterType
            quality,                // quality
            'rain'                  // textureType
        );

        const entity = this.getElement(id);
        entity.setAttribute('position', `${x} ${y} ${z}`);

        const particleSystem = entity.object3D.children[0];
        const geometry = particleSystem.geometry;
        const count = geometry.attributes.position.count;

        const positions = geometry.attributes.position.array;
        const velocities = geometry.attributes.velocity.array;
        const startTimes = geometry.attributes.startTime.array;
        const originalY = y;

        const customAnimate = () => {
            const time = Date.now() * 0.001;
            
            for (let i = 0; i < count; i++) {
                const i3 = i * 3;
                
                // Add slight wind effect
                const windStrength = Math.sin(time * 0.5) * 0.5;
                velocities[i3] = windStrength;  // X direction wind
                
                // Update positions
                positions[i3] += velocities[i3] * 0.016;               // X position with wind
                positions[i3 + 1] += velocities[i3 + 1] * 0.016;      // Y position (falling)
                positions[i3 + 2] += velocities[i3 + 2] * 0.016;      // Z position
                
                // Respawn logic
                if (positions[i3 + 1] < originalY - 30) {
                    // Reset to top with random position
                    positions[i3] = (Math.random() - 0.5) * 30 + x;
                    positions[i3 + 1] = originalY + Math.random() * 5;
                    positions[i3 + 2] = (Math.random() - 0.5) * 30 + z;
                    
                    // Set velocities for streaking rain effect
                    velocities[i3] = (Math.random() - 0.5) * 2;       // Slight X variation
                    velocities[i3 + 1] = -20 - Math.random() * 10;    // Fast downward
                    velocities[i3 + 2] = (Math.random() - 0.5) * 2;   // Slight Z variation
                }
            }

            geometry.attributes.position.needsUpdate = true;
            geometry.attributes.velocity.needsUpdate = true;
            
            requestAnimationFrame(customAnimate);
        };

        // Initial setup
        for (let i = 0; i < count; i++) {
            const i3 = i * 3;
            
            // Initial positions - spread out in volume
            positions[i3] = (Math.random() - 0.5) * 30 + x;
            positions[i3 + 1] = originalY + Math.random() * 30;
            positions[i3 + 2] = (Math.random() - 0.5) * 30 + z;
            
            // Initial velocities - mostly downward
            velocities[i3] = (Math.random() - 0.5) * 2;       // Small X spread
            velocities[i3 + 1] = -20 - Math.random() * 10;    // Fast downward
            velocities[i3 + 2] = (Math.random() - 0.5) * 2;   // Small Z spread
            
            startTimes[i] = Math.random() * Math.PI * 2;
        }

        // Material settings for rain streaks
        particleSystem.material.size = 0.1;
        particleSystem.material.opacity = 0.4;
        particleSystem.material.transparent = true;
        particleSystem.material.depthWrite = false;
        particleSystem.material.blending = THREE.AdditiveBlending;
        
        // Make raindrops appear more stretched
        particleSystem.material.scale = 4.0;
        
        // Start animation
        customAnimate();

        // Update attributes
        geometry.attributes.position.needsUpdate = true;
        geometry.attributes.velocity.needsUpdate = true;
        geometry.attributes.startTime.needsUpdate = true;

        return id;
    }

    addExplosionEffect(x = 0, y = 0, z = 0, intensity = 1, quality = 'medium') {
        // Create main explosion effect
        const explosionId = this.addParticleSystem(
            300 * intensity,
            0.15,
            '#ff4400',
            15 * intensity,
            0.8,
            0.1,
            'sphere',
            'point',
            quality,
            'spark'
        );

        // Create smoke effect
        const smokeId = this.addParticleSystem(
            200 * intensity,
            0.4,
            '#444444',
            3 * intensity,
            2.5,
            0.2,
            'sphere',
            'point',
            quality,
            'smoke'
        );

        const explosionEntity = this.getElement(explosionId);
        const smokeEntity = this.getElement(smokeId);
        
        explosionEntity.setAttribute('position', `${x} ${y} ${z}`);
        smokeEntity.setAttribute('position', `${x} ${y} ${z}`);

        // Set up explosion particles
        const explosionSystem = explosionEntity.object3D.children[0];
        const explosionGeometry = explosionSystem.geometry;
        const explosionCount = explosionGeometry.attributes.position.count;

        const expPositions = explosionGeometry.attributes.position.array;
        const expVelocities = explosionGeometry.attributes.velocity.array;
        const expColors = explosionGeometry.attributes.color.array;
        const expSizes = explosionGeometry.attributes.size.array;

        // Set up smoke particles
        const smokeSystem = smokeEntity.object3D.children[0];
        const smokeGeometry = smokeSystem.geometry;
        const smokeCount = smokeGeometry.attributes.position.count;

        const smokePositions = smokeGeometry.attributes.position.array;
        const smokeVelocities = smokeGeometry.attributes.velocity.array;
        const smokeColors = smokeGeometry.attributes.color.array;
        const smokeSizes = smokeGeometry.attributes.size.array;

        // Initialize explosion particles
        for (let i = 0; i < explosionCount; i++) {
            const i3 = i * 3;
            
            expPositions[i3] = 0;
            expPositions[i3 + 1] = 0;
            expPositions[i3 + 2] = 0;

            const phi = Math.random() * Math.PI * 2;
            const theta = Math.random() * Math.PI;
            const speed = (15 + Math.random() * 10) * intensity;

            expVelocities[i3] = Math.sin(theta) * Math.cos(phi) * speed;
            expVelocities[i3 + 1] = Math.sin(theta) * Math.sin(phi) * speed;
            expVelocities[i3 + 2] = Math.cos(theta) * speed;

            expColors[i3] = 1;
            expColors[i3 + 1] = 0.8;
            expColors[i3 + 2] = 0.2;

            expSizes[i] = 0.2 + Math.random() * 0.2;
        }

        // Initialize smoke particles
        for (let i = 0; i < smokeCount; i++) {
            const i3 = i * 3;
            
            smokePositions[i3] = 0;
            smokePositions[i3 + 1] = 0;
            smokePositions[i3 + 2] = 0;

            const phi = Math.random() * Math.PI * 2;
            const theta = Math.random() * Math.PI;
            const speed = (2 + Math.random() * 4) * intensity;

            smokeVelocities[i3] = Math.sin(theta) * Math.cos(phi) * speed;
            smokeVelocities[i3 + 1] = Math.sin(theta) * Math.sin(phi) * speed + 2;
            smokeVelocities[i3 + 2] = Math.cos(theta) * speed;

            const gray = 0.2 + Math.random() * 0.2;
            smokeColors[i3] = gray;
            smokeColors[i3 + 1] = gray;
            smokeColors[i3 + 2] = gray;

            smokeSizes[i] = 0.4 + Math.random() * 0.6;
        }

        let startTime = Date.now();
        const explosionDuration = 800;
        const smokeDuration = 2500;

        const customAnimate = () => {
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;
            const explosionProgress = Math.min(elapsed / explosionDuration, 1);
            const smokeProgress = Math.min(elapsed / smokeDuration, 1);

            // Animate explosion particles
            if (explosionProgress < 1) {
                for (let i = 0; i < explosionCount; i++) {
                    const i3 = i * 3;
                    
                    const speedFactor = explosionProgress < 0.2 ? 1 : 0.95;

                    expPositions[i3] += expVelocities[i3] * 0.016;
                    expPositions[i3 + 1] += expVelocities[i3 + 1] * 0.016;
                    expPositions[i3 + 2] += expVelocities[i3 + 2] * 0.016;

                    expVelocities[i3] *= speedFactor;
                    expVelocities[i3 + 1] *= speedFactor;
                    expVelocities[i3 + 2] *= speedFactor;

                    expColors[i3] = 1;
                    expColors[i3 + 1] = 0.8 - (explosionProgress * 0.8);
                    expColors[i3 + 2] = 0.2 - (explosionProgress * 0.2);

                    expSizes[i] *= 0.98;
                }

                const explosionOpacity = 1 - Math.pow(explosionProgress, 2);
                explosionSystem.material.opacity = explosionOpacity;

                explosionGeometry.attributes.position.needsUpdate = true;
                explosionGeometry.attributes.color.needsUpdate = true;
                explosionGeometry.attributes.size.needsUpdate = true;
            }

            // Animate smoke particles
            if (smokeProgress < 1) {
                for (let i = 0; i < smokeCount; i++) {
                    const i3 = i * 3;

                    smokePositions[i3] += smokeVelocities[i3] * 0.016;
                    smokePositions[i3 + 1] += smokeVelocities[i3 + 1] * 0.016;
                    smokePositions[i3 + 2] += smokeVelocities[i3 + 2] * 0.016;

                    // Add turbulence to smoke
                    smokeVelocities[i3] += (Math.random() - 0.5) * 0.1;
                    smokeVelocities[i3 + 1] += (Math.random() - 0.5) * 0.1 + 0.1;
                    smokeVelocities[i3 + 2] += (Math.random() - 0.5) * 0.1;

                    // Slow down smoke over time
                    smokeVelocities[i3] *= 0.98;
                    smokeVelocities[i3 + 1] *= 0.98;
                    smokeVelocities[i3 + 2] *= 0.98;

                    smokeSizes[i] *= 1.01; // Gradually increase smoke particle size
                }

                const smokeOpacity = Math.min(1, smokeProgress < 0.2 ? smokeProgress * 5 : 1) * (1 - smokeProgress);
                smokeSystem.material.opacity = smokeOpacity;

                smokeGeometry.attributes.position.needsUpdate = true;
                smokeGeometry.attributes.size.needsUpdate = true;
            }

            if (explosionProgress < 1 || smokeProgress < 1) {
                requestAnimationFrame(customAnimate);
            } else {
                // Clean up
                this.scene.removeChild(explosionEntity);
                this.scene.removeChild(smokeEntity);
                this.elements.delete(explosionId);
                this.elements.delete(smokeId);
            }
        };

        // Set up explosion material
        explosionSystem.material.vertexColors = true;
        explosionSystem.material.transparent = true;
        explosionSystem.material.depthWrite = false;
        explosionSystem.material.blending = THREE.AdditiveBlending;

        // Set up smoke material
        smokeSystem.material.vertexColors = true;
        smokeSystem.material.transparent = true;
        smokeSystem.material.depthWrite = false;
        smokeSystem.material.blending = THREE.AdditiveBlending;

        // Start animation
        customAnimate();

        return [explosionId, smokeId];
    }

    addBloomEffect(strength = 1.5, radius = 0.4, threshold = 0.85) {
        const renderer = this.scene.renderer;
        if (!renderer) return;

        const composer = new EffectComposer(renderer);
        const renderPass = new RenderPass(this.scene.object3D, this.scene.camera);
        composer.addPass(renderPass);

        const bloomPass = new UnrealBloomPass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            strength,
            radius,
            threshold
        );
        composer.addPass(bloomPass);

        this.scene.renderer = composer;
    }

    addFilmEffect(noiseIntensity = 0.35, scanlinesIntensity = 0.25, scanlinesCount = 648, grayscale = false) {
        const renderer = this.scene.renderer;
        if (!renderer) return;

        const composer = new EffectComposer(renderer);
        const renderPass = new RenderPass(this.scene.object3D, this.scene.camera);
        composer.addPass(renderPass);

        const filmPass = new FilmPass(
            noiseIntensity,
            scanlinesIntensity,
            scanlinesCount,
            grayscale
        );
        composer.addPass(filmPass);

        this.scene.renderer = composer;
    }

    addEnvironmentMap(rightUrl, leftUrl, topUrl, bottomUrl, frontUrl, backUrl) {
        const loader = new THREE.CubeTextureLoader();
        const textureCube = loader.load([
            rightUrl, leftUrl,
            topUrl, bottomUrl,
            frontUrl, backUrl
        ]);
        
        this.scene.object3D.background = textureCube;
        
        this.elements.forEach(element => {
            const mesh = element.getObject3D('mesh');
            if (mesh && mesh.material) {
                mesh.material.envMap = textureCube;
                mesh.material.needsUpdate = true;
            }
        });
    }

    enableShadows(mapSize = 1024, bias = -0.001, type = THREE.PCFSoftShadowMap) {
        this.scene.renderer.shadowMap.enabled = true;
        this.scene.renderer.shadowMap.type = type;
        
        this.elements.forEach(element => {
            const light = element.getObject3D('light');
            if (light) {
                light.castShadow = true;
                light.shadow.mapSize.width = mapSize;
                light.shadow.mapSize.height = mapSize;
                light.shadow.bias = bias;
            }
            
            const mesh = element.getObject3D('mesh');
            if (mesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });
    }

    enableVRControls(hand = 'right', modelColor = '#ffffff', rayOrigin = 'grip') {
        const id = this.generateId('vr-controls');
        const controls = document.createElement('a-entity');
        
        controls.setAttribute('laser-controls', {
            hand: hand,
            model: true,
            color: modelColor,
            rayOrigin: rayOrigin
        });
        
        controls.setAttribute('raycaster', {
            objects: '.interactive',
            far: 20,
            lineColor: modelColor,
            lineOpacity: 0.5
        });
        
        controls.id = id;
        this.scene.appendChild(controls);
        this.elements.set(id, controls);
        return id;
    }

    enablePhysics(gravity = -9.8, maxSteps = 60, debug = false) {
        this.scene.setAttribute('physics', {
            gravity: gravity,
            maxSteps: maxSteps,
            debug: debug
        });
    }

    addPhysicsBody(id, type = 'dynamic', mass = 1, shape = 'auto', restitution = 0.3, friction = 0.5) {
        const element = this.getElement(id);
        element.setAttribute('dynamic-body', {
            type: type,
            mass: mass,
            shape: shape,
            restitution: restitution,
            friction: friction
        });
        return this;
    }

    createGroup() {
        const id = this.generateId('group');
        const group = document.createElement('a-entity');
        group.id = id;
        this.scene.appendChild(group);
        this.elements.set(id, group);
        return id;
    }

    addToGroup(groupId, childId) {
        const group = this.getElement(groupId);
        const child = this.getElement(childId);
        group.appendChild(child);
        return this;
    }

    addAmbientLight(color = '#ffffff', intensity = 0.5) {
        const id = this.generateId('ambient-light');
        const light = document.createElement('a-light');
        light.setAttribute('type', 'ambient');
        light.setAttribute('color', color);
        light.setAttribute('intensity', intensity);
        light.id = id;
        this.scene.appendChild(light);
        this.elements.set(id, light);
        return id;
    }

    addDirectionalLight(color = '#ffffff', intensity = 1, x = 0, y = 1, z = -1) {
        const id = this.generateId('directional-light');
        const light = document.createElement('a-light');
        light.setAttribute('type', 'directional');
        light.setAttribute('color', color);
        light.setAttribute('intensity', intensity);
        light.setAttribute('position', `${x} ${y} ${z}`);
        light.id = id;
        this.scene.appendChild(light);
        this.elements.set(id, light);
        return id;
    }

    setVisibility(id, visible) {
        const element = this.getElement(id);
        element.setAttribute('visible', visible);
        return this;
    }

    remove(id) {
        const element = this.getElement(id);
        if (element) {
            element.parentNode.removeChild(element);
            this.elements.delete(id);
        }
        return this;
    }

    addOrbitControls(targetX = 0, targetY = 0, targetZ = 0, enableDamping = true, dampingFactor = 0.25, rotateSpeed = 0.25) {
        const camera = this.scene.querySelector('a-camera');
        if (!camera) {
            const id = this.generateId('camera');
            const newCamera = document.createElement('a-camera');
            newCamera.setAttribute('orbit-controls', {
                target: `${targetX} ${targetY} ${targetZ}`,
                enableDamping: enableDamping,
                dampingFactor: dampingFactor,
                rotateSpeed: rotateSpeed
            });
            newCamera.id = id;
            this.scene.appendChild(newCamera);
            this.elements.set(id, newCamera);
            return id;
        } else {
            camera.setAttribute('orbit-controls', {
                target: `${targetX} ${targetY} ${targetZ}`,
                enableDamping: enableDamping,
                dampingFactor: dampingFactor,
                rotateSpeed: rotateSpeed
            });
            return camera.id;
        }
    }

    clone(id, x = null, y = null, z = null) {
        const original = this.getElement(id);
        const clone = original.cloneNode(true);
        const newId = this.generateId(id.split('_')[0]);
        clone.id = newId;
        
        if (x !== null && y !== null && z !== null) {
            clone.setAttribute('position', `${x} ${y} ${z}`);
        }
        
        this.scene.appendChild(clone);
        this.elements.set(newId, clone);
        return newId;
    }

    lookAt(id, targetId) {
        const element = this.getElement(id);
        element.setAttribute('look-at', `#${targetId}`);
        return this;
    }

    addEventListener(id, eventName, callback) {
        const element = this.getElement(id);
        element.addEventListener(eventName, callback);
        return this;
    }

    removeEventListener(id, eventName, callback) {
        const element = this.getElement(id);
        element.removeEventListener(eventName, callback);
        return this;
    }

    makeInteractive(id, clickable = true, hoverable = true) {
        const element = this.getElement(id);
        element.classList.add('interactive');
        
        if (clickable) {
            element.setAttribute('class', element.getAttribute('class') + ' clickable');
        }
        
        if (hoverable) {
            element.setAttribute('class', element.getAttribute('class') + ' hoverable');
        }
        
        return this;
    }

    addOctahedron(x = 0, y = 0, z = 0, radius = 1, color = 'red', detail = 0) {
        const id = this.generateId('octahedron');
        const octahedron = document.createElement('a-entity');
        octahedron.setAttribute('geometry', {
            primitive: 'octahedron',
            radius: radius,
            detail: detail
        });
        octahedron.setAttribute('position', `${x} ${y} ${z}`);
        octahedron.setAttribute('material', { color: color });
        octahedron.id = id;
        this.scene.appendChild(octahedron);
        this.elements.set(id, octahedron);
        return id;
    }

    addDodecahedron(x = 0, y = 0, z = 0, radius = 1, color = 'red', detail = 0) {
        const id = this.generateId('dodecahedron');
        const dodecahedron = document.createElement('a-entity');
        dodecahedron.setAttribute('geometry', {
            primitive: 'dodecahedron',
            radius: radius,
            detail: detail
        });
        dodecahedron.setAttribute('position', `${x} ${y} ${z}`);
        dodecahedron.setAttribute('material', { color: color });
        dodecahedron.id = id;
        this.scene.appendChild(dodecahedron);
        this.elements.set(id, dodecahedron);
        return id;
    }

    addIcosahedron(x = 0, y = 0, z = 0, radius = 1, color = 'red', detail = 0) {
        const id = this.generateId('icosahedron');
        const icosahedron = document.createElement('a-entity');
        icosahedron.setAttribute('geometry', {
            primitive: 'icosahedron',
            radius: radius,
            detail: detail
        });
        icosahedron.setAttribute('position', `${x} ${y} ${z}`);
        icosahedron.setAttribute('material', { color: color });
        icosahedron.id = id;
        this.scene.appendChild(icosahedron);
        this.elements.set(id, icosahedron);
        return id;
    }

    addTetrahedron(x = 0, y = 0, z = 0, radius = 1, color = 'red', detail = 0) {
        const id = this.generateId('tetrahedron');
        const tetrahedron = document.createElement('a-entity');
        tetrahedron.setAttribute('geometry', {
            primitive: 'tetrahedron',
            radius: radius,
            detail: detail
        });
        tetrahedron.setAttribute('position', `${x} ${y} ${z}`);
        tetrahedron.setAttribute('material', { color: color });
        tetrahedron.id = id;
        this.scene.appendChild(tetrahedron);
        this.elements.set(id, tetrahedron);
        return id;
    }

    addRing(x = 0, y = 0, z = 0, radiusInner = 0.5, radiusOuter = 1, color = 'red', segments = 32) {
        const id = this.generateId('ring');
        const ring = document.createElement('a-ring');
        ring.setAttribute('position', `${x} ${y} ${z}`);
        ring.setAttribute('radius-inner', radiusInner);
        ring.setAttribute('radius-outer', radiusOuter);
        ring.setAttribute('segments-theta', segments);
        ring.setAttribute('color', color);
        ring.id = id;
        this.scene.appendChild(ring);
        this.elements.set(id, ring);
        return id;
    }

    addSpotLight(x = 0, y = 0, z = 0, color = '#ffffff', intensity = 1, angle = 60, penumbra = 0, decay = 1) {
        const id = this.generateId('spotlight');
        const light = document.createElement('a-light');
        light.setAttribute('type', 'spot');
        light.setAttribute('position', `${x} ${y} ${z}`);
        light.setAttribute('color', color);
        light.setAttribute('intensity', intensity);
        light.setAttribute('angle', angle);
        light.setAttribute('penumbra', penumbra);
        light.setAttribute('decay', decay);
        light.id = id;
        this.scene.appendChild(light);
        this.elements.set(id, light);
        return id;
    }

    addPointLight(x = 0, y = 0, z = 0, color = '#ffffff', intensity = 1, distance = 0, decay = 1) {
        const id = this.generateId('pointlight');
        const light = document.createElement('a-light');
        light.setAttribute('type', 'point');
        light.setAttribute('position', `${x} ${y} ${z}`);
        light.setAttribute('color', color);
        light.setAttribute('intensity', intensity);
        light.setAttribute('distance', distance);
        light.setAttribute('decay', decay);
        light.id = id;
        this.scene.appendChild(light);
        this.elements.set(id, light);
        return id;
    }

    addRaycastListener(id, onIntersect, onIntersectEnd) {
        const element = this.getElement(id);
        element.setAttribute('raycaster', {
            objects: '.interactive',
            far: 20,
            interval: 100
        });

        element.addEventListener('raycaster-intersection', onIntersect);
        element.addEventListener('raycaster-intersection-cleared', onIntersectEnd);
        return this;
    }

    addOutlineEffect(id, thickness = 2, color = '#000000') {
        const element = this.getElement(id);
        const mesh = element.getObject3D('mesh');
        if (!mesh) return this;

        const outlineMaterial = new THREE.MeshBasicMaterial({
            color: color,
            side: THREE.BackSide
        });

        const outlineMesh = new THREE.Mesh(mesh.geometry, outlineMaterial);
        outlineMesh.scale.multiplyScalar(1 + thickness * 0.01);
        mesh.add(outlineMesh);
        return this;
    }

    addGlow(id, intensity = 1, color = '#ffffff', scale = 1.2) {
        const element = this.getElement(id);
        const glowElement = document.createElement('a-entity');
        glowElement.setAttribute('geometry', element.getAttribute('geometry'));
        glowElement.setAttribute('material', {
            shader: 'flat',
            color: color,
            opacity: 0.5,
            transparent: true
        });
        glowElement.setAttribute('scale', `${scale} ${scale} ${scale}`);
        element.appendChild(glowElement);
        return this;
    }

    addPathAnimation(id, points, duration = 5000, loop = true) {
        const element = this.getElement(id);
        const path = points.flat().join(',');
        element.setAttribute('alongpath', {
            path: path,
            dur: duration,
            loop: loop
        });
        return this;
    }

    addBezierAnimation(id, start, control1, control2, end, duration = 5000, loop = true) {
        const points = [
            start,
            control1,
            control2,
            end
        ].map(p => `${p.x} ${p.y} ${p.z}`).join(', ');

        const element = this.getElement(id);
        element.setAttribute('alongpath', {
            curve: points,
            dur: duration,
            loop: loop
        });
        return this;
    }

    addTriggerZone(x = 0, y = 0, z = 0, radius = 1, onEnter, onExit) {
        const id = this.generateId('trigger');
        const trigger = document.createElement('a-sphere');
        trigger.setAttribute('position', `${x} ${y} ${z}`);
        trigger.setAttribute('radius', radius);
        trigger.setAttribute('material', {
            opacity: 0,
            transparent: true
        });
        trigger.setAttribute('class', 'interactive trigger-zone');
        
        if (onEnter) trigger.addEventListener('mouseenter', onEnter);
        if (onExit) trigger.addEventListener('mouseleave', onExit);
        
        trigger.id = id;
        this.scene.appendChild(trigger);
        this.elements.set(id, trigger);
        return id;
    }

    addPortal(x = 0, y = 0, z = 0, radius = 2, destination = { x: 0, y: 0, z: -10 }, onTrigger = null) {
        const id = this.generateId('portal');
        const portal = document.createElement('a-entity');
        portal.setAttribute('position', `${x} ${y} ${z}`);

        const generateTechPattern = (size) => {
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            let ctx = canvas.getContext('2d');
            
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, size, size);
            
            ctx.strokeStyle = '#444444';
            ctx.lineWidth = 2;
            
            for (let i = 0; i < 20; i++) {
                const x = Math.random() * size;
                const y = Math.random() * size;
                const length = 10 + Math.random() * 30;
                const angle = Math.random() * Math.PI * 2;
                
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x + Math.cos(angle) * length, y + Math.sin(angle) * length);
                ctx.stroke();
            }
            
            ctx.strokeStyle = '#666666';
            for (let i = 0; i < 10; i++) {
                const centerX = Math.random() * size;
                const centerY = Math.random() * size;
                const radius = 5 + Math.random() * 15;
                
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.stroke();
            }
            
            ctx.fillStyle = '#888888';
            ctx.font = '12px monospace';
            for (let i = 0; i < 15; i++) {
                const text = Math.random().toString(16).substring(2, 4).toUpperCase();
                ctx.fillText(text, Math.random() * (size - 20), Math.random() * (size - 10));
            }
            
            return canvas.toDataURL();
        };

        const mainFrame = document.createElement('a-torus');
        mainFrame.setAttribute('radius', radius);
        mainFrame.setAttribute('radius-tubular', 0.2);
        mainFrame.setAttribute('segments-tubular', 64);
        mainFrame.setAttribute('segments-radial', 64);
        mainFrame.setAttribute('material', {
            color: '#888888',
            metalness: 0.9,
            roughness: 0.3,
            src: generateTechPattern(512),
            normalTextureRepeat: '8 1',
            normalScale: '0.5 0.5',
            emissive: '#2200ff',
            emissiveIntensity: 0.2,
            repeat: '8 1'
        });

        const techDetails = document.createElement('a-entity');
        const techParts = [];
        const segments = 12;
        
        for (let i = 0; i < segments; i++) {
            const angle = (i / segments) * Math.PI * 2;
            const detail = document.createElement('a-box');
            const distance = radius - 0.1;
            
            detail.setAttribute('width', '0.2');
            detail.setAttribute('height', '0.4');
            detail.setAttribute('depth', '0.1');
            detail.setAttribute('position', {
                x: Math.cos(angle) * distance,
                y: Math.sin(angle) * distance,
                z: 0
            });
            detail.setAttribute('rotation', {
                x: 0,
                y: 0,
                z: (angle * 180 / Math.PI) + 90
            });
            detail.setAttribute('material', {
                color: '#444444',
                metalness: 0.7,
                roughness: 0.3,
                emissive: '#2200ff',
                emissiveIntensity: 0.1
            });
            
            techDetails.appendChild(detail);
            techParts.push(detail);
        }

        const hole = document.createElement('a-circle');
        hole.setAttribute('radius', radius - 0.3);
        hole.setAttribute('position', '0 0 0.06');
        hole.setAttribute('material', {
            shader: 'portal',
            side: 'double',
            transparent: true,
            opacity: 0.8
        });

        const innerGlow = document.createElement('a-ring');
        innerGlow.setAttribute('radius-inner', radius - 0.4);
        innerGlow.setAttribute('radius-outer', radius - 0.2);
        innerGlow.setAttribute('position', '0 0 0.05');
        innerGlow.setAttribute('material', {
            shader: 'flat',
            color: '#9933ff',
            side: 'double',
            transparent: true,
            opacity: 0.4
        });

        const techLines = document.createElement('a-ring');
        techLines.setAttribute('radius-inner', radius - 0.25);
        techLines.setAttribute('radius-outer', radius - 0.15);
        techLines.setAttribute('position', '0 0 0.07');
        techLines.setAttribute('theta-length', 360);
        techLines.setAttribute('theta-start', 0);
        techLines.setAttribute('material', {
            shader: 'flat',
            color: '#4411aa',
            transparent: true,
            opacity: 0.8,
            src: generateTechPattern(256)
        });

        portal.appendChild(mainFrame);
        portal.appendChild(techDetails);
        portal.appendChild(hole);
        portal.appendChild(innerGlow);
        portal.appendChild(techLines);

        this.scene.addEventListener('loaded', () => {
            const camera = document.querySelector('a-camera');
            let isInPortal = false;
            this.addPortalEffect(x, y, z + 0.2, 3, 2, 'ultra');
            this.addPortalEffect(x, y, z - 0.2, 3, 2, 'ultra');


            const startTime = Date.now();
            const animate = () => {
                const elapsed = (Date.now() - startTime) / 1000;
                
                const baseGlow = 0.2 + Math.sin(elapsed * 2) * 0.1;
                innerGlow.setAttribute('material', 'opacity', baseGlow);
                
                techParts.forEach((detail, index) => {
                    const offset = index * (Math.PI * 2 / segments);
                    const intensity = 0.1 + Math.sin(elapsed * 3 + offset) * 0.1;
                    detail.setAttribute('material', 'emissiveIntensity', intensity);
                });
                
                techLines.setAttribute('rotation', `0 0 ${elapsed * 10}`);
                
                if (camera) {
                    const cameraPos = camera.getAttribute('position');
                    const portalPos = portal.getAttribute('position');
                    
                    const distanceToPortal = Math.sqrt(
                        Math.pow(cameraPos.x - portalPos.x, 2) +
                        Math.pow(cameraPos.y - portalPos.y, 2) +
                        Math.pow(cameraPos.z - portalPos.z, 2)
                    );
                    
                    const isIntersecting = distanceToPortal <= radius - 0.3 && 
                        Math.abs(cameraPos.z - portalPos.z) <= 0.5;
                    
                    if (isIntersecting && !isInPortal) {
                        isInPortal = true;
                        if (onTrigger) {
                            onTrigger(camera, destination);
                        } else {
                            camera.setAttribute('position', `${destination.x} ${destination.y} ${destination.z}`);
                        }
                    } else if (!isIntersecting && isInPortal) {
                        isInPortal = false;
                    }
                }
                
                requestAnimationFrame(animate);
            };
            animate();
        });

        portal.id = id;
        this.scene.appendChild(portal);
        this.elements.set(id, portal);
        return id;
    }


    addPortalOld(x = 0, y = 0, z = 0, width = 2, height = 3, destination = { x: 0, y: 0, z: -10 }, onTrigger = null) {
        const id = this.generateId('portal');
        const portal = document.createElement('a-entity');
        portal.setAttribute('position', `${x} ${y} ${z}`);
        
        const frame = document.createElement('a-box');
        frame.setAttribute('width', width + 0.2);
        frame.setAttribute('height', height + 0.2);
        frame.setAttribute('depth', 0.1);
        frame.setAttribute('material', {
            color: '#000000',
            metalness: 0.8,
            roughness: 0.2
        });
        
        const hole = document.createElement('a-plane');
        hole.setAttribute('width', width);
        hole.setAttribute('height', height);
        hole.setAttribute('position', '0 0 0.06');
        hole.setAttribute('material', {
            shader: 'portal',
            side: 'double'
        });
        
        portal.appendChild(frame);
        portal.appendChild(hole);
        
        // Add position checking component
        this.scene.addEventListener('loaded', () => {
            const camera = document.querySelector('a-camera');
            let isInPortal = false;
            
            // Check camera position every frame
            this.scene.renderer.setAnimationLoop(() => {
                if (!camera) return;
                
                const cameraPos = camera.getAttribute('position');
                const portalPos = portal.getAttribute('position');
                
                // Check if camera is within portal bounds
                const isIntersecting = (
                    Math.abs(cameraPos.x - portalPos.x) <= width / 2 &&
                    Math.abs(cameraPos.y - portalPos.y) <= height / 2 &&
                    Math.abs(cameraPos.z - portalPos.z) <= 0.5  // Depth threshold
                );
                
                // Only trigger once when entering portal
                if (isIntersecting && !isInPortal) {
                    isInPortal = true;
                    if (onTrigger) {
                        // Call custom callback if provided
                        onTrigger(camera, destination);
                    } else {
                        // Default behavior: teleport camera
                        camera.setAttribute('position', `${destination.x} ${destination.y} ${destination.z}`);
                    }
                } else if (!isIntersecting && isInPortal) {
                    isInPortal = false;
                }
            });
        });
        
        portal.id = id;
        this.scene.appendChild(portal);
        this.elements.set(id, portal);
        return id;
    }

    addReflectiveSurface(x = 0, y = 0, z = 0, width = 10, height = 10) {
        const id = this.generateId('mirror');
        const mirror = document.createElement('a-entity');
        mirror.setAttribute('position', `${x} ${y} ${z}`);
        
        const geometry = new THREE.PlaneGeometry(width, height);
        const groundMirror = new THREE.Reflector(geometry, {
            clipBias: 0.003,
            textureWidth: window.innerWidth * window.devicePixelRatio,
            textureHeight: window.innerHeight * window.devicePixelRatio,
            color: 0x777777
        });
        
        mirror.setObject3D('mesh', groundMirror);
        mirror.id = id;
        this.scene.appendChild(mirror);
        this.elements.set(id, mirror);
        return id;
    }

    addWaterSurface(x = 0, y = 0, z = 0, width = 10, height = 10) {
        const id = this.generateId('water');
        const water = document.createElement('a-entity');
        water.setAttribute('position', `${x} ${y} ${z}`);
        
        const waterGeometry = new THREE.PlaneGeometry(width, height);
        const water3D = new THREE.Water(waterGeometry, {
            textureWidth: 512,
            textureHeight: 512,
            waterNormals: new THREE.TextureLoader().load('path/to/waternormals.jpg', (texture) => {
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            }),
            alpha: 1.0,
            sunDirection: new THREE.Vector3(),
            sunColor: 0xffffff,
            waterColor: 0x001e0f,
            distortionScale: 3.7,
            fog: false
        });
        
        water.setObject3D('mesh', water3D);
        water.id = id;
        this.scene.appendChild(water);
        this.elements.set(id, water);
        return id;
    }

    addLensFlare(x = 0, y = 0, z = 0, color = '#ffffff', size = 100) {
        const id = this.generateId('lensflare');
        const lensflare = document.createElement('a-entity');
        lensflare.setAttribute('position', `${x} ${y} ${z}`);
        
        // Create sprite material
        const spriteMaterial = new THREE.SpriteMaterial({
            map: new THREE.TextureLoader().load('/path/to/flare.png'),
            color: color,
            transparent: true,
            blending: THREE.AdditiveBlending,
            opacity: 0.7
        });

        // Create sprite
        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.scale.set(size, size, 1);

        // Add sprite to entity
        lensflare.setObject3D('sprite', sprite);
        lensflare.id = id;
        
        // Add billboard behavior to always face camera
        lensflare.setAttribute('look-at', '[camera]');
        
        this.scene.appendChild(lensflare);
        this.elements.set(id, lensflare);
        
        // Add distance-based scaling
        const camera = document.querySelector('a-camera').object3D;
        const updateScale = () => {
            if (camera && sprite) {
                const distance = camera.position.distanceTo(sprite.position);
                const scale = Math.max(size * (distance / 100), size * 0.5);
                sprite.scale.set(scale, scale, 1);
            }
        };
        
        // Update on render
        this.scene.renderer.setAnimationLoop(() => {
            updateScale();
        });

        return id;
    }

    addPortalEffect(x = 0, y = 0, z = 0, radius = 2, intensity = 1, quality = 'medium') {
        const particleVertexShader = `
            attribute float size;
            attribute vec3 customColor;
            attribute float phase;
            attribute float opacity;
            varying vec3 vColor;
            varying float vOpacity;
            varying vec2 vUv;
            varying float vPhase;
            uniform float time;
            void main() {
                vColor = customColor;
                vOpacity = opacity;
                vPhase = phase;
                vUv = uv;
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                float distanceFromCenter = length(position.xy);
                float sizeAttenuation = size * (300.0 / -mvPosition.z);
                sizeAttenuation *= (1.0 + 0.2 * sin(phase + time));
                gl_PointSize = sizeAttenuation;
                gl_Position = projectionMatrix * mvPosition;
            }
        `;

        const particleFragmentShader = `
            varying vec3 vColor;
            varying float vOpacity;
            varying vec2 vUv;
            varying float vPhase;
            uniform float time;
            void main() {
                vec2 center = vec2(0.5, 0.5);
                float dist = length(gl_PointCoord - center);
                float alpha = (1.0 - smoothstep(0.0, 0.5, dist)) * vOpacity;
                float pulse = sin(vPhase + time * 2.0) * 0.5 + 0.5;
                float innerGlow = (1.0 - dist * 2.0) * 0.5;
                float ripple = sin(dist * 20.0 - time * 3.0) * 0.1;
                vec3 finalColor = vColor + vec3(innerGlow) + vec3(ripple) * pulse;
                gl_FragColor = vec4(finalColor, alpha);
            }
        `;

        const cyberPortalShader = {
            vertexShader: `
                varying vec2 vUv;
                varying vec3 vPosition;
                uniform float time;
                void main() {
                    vUv = uv;
                    vPosition = position;
                    vec3 pos = position;
                    float elevation = sin(position.x * 3.0 + time) * cos(position.y * 3.0 + time) * 0.05;
                    pos += normal * elevation;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
                }
            `,
            fragmentShader: `
                varying vec2 vUv;
                varying vec3 vPosition;
                uniform float time;
                uniform vec3 portalColor;
                
                float grid(vec2 uv, float size) {
                    vec2 grid = fract(uv * size);
                    return (step(0.95, grid.x) + step(0.95, grid.y)) * 0.5;
                }
                
                float random(vec2 st) {
                    return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
                }
                
                void main() {
                    vec2 uv = vUv;
                    vec3 finalColor = portalColor;
                    
                    float baseGrid = grid(uv, 20.0);
                    
                    float dataLine = step(0.98, fract(uv.y * 30.0 - time));
                    dataLine += step(0.98, fract(uv.x * 30.0 - time * 0.7));
                    
                    float noise = random(uv + time * 0.1) * 0.1;
                    
                    vec2 center = vec2(0.5, 0.5);
                    float dist = length(uv - center);
                    float circle = 1.0 - smoothstep(0.0, 0.5, dist);
                    
                    float hexGrid = 0.0;
                    for(float i = 0.0; i < 3.0; i++) {
                        vec2 hexUv = uv * (10.0 + i * 5.0);
                        hexUv.x += time * (0.1 + i * 0.1);
                        hexGrid += grid(hexUv, 1.0) * (0.2 - i * 0.05);
                    }
                    
                    float pulse = sin(dist * 10.0 - time * 2.0) * 0.5 + 0.5;
                    
                    float streams = 0.0;
                    for(float i = 0.0; i < 5.0; i++) {
                        float offset = random(vec2(i)) * 6.28;
                        streams += step(0.98, fract(uv.y * 20.0 + uv.x * 5.0 - time + offset));
                    }
                    
                    vec3 gridColor = vec3(baseGrid + hexGrid) * vec3(0.5, 0.8, 1.0);
                    vec3 dataColor = vec3(dataLine + streams) * vec3(0.0, 1.0, 0.8);
                    vec3 pulseColor = vec3(pulse) * portalColor;
                    
                    finalColor = mix(finalColor, gridColor + dataColor + pulseColor, circle);
                    
                    finalColor += noise * vec3(0.5, 0.8, 1.0);
                    finalColor += circle * portalColor * 0.2;
                    
                    float edgeGlow = smoothstep(0.4, 0.5, dist) * smoothstep(0.6, 0.5, dist);
                    finalColor += edgeGlow * portalColor;
                    
                    float alpha = circle * (0.8 + pulse * 0.2);
                    
                    gl_FragColor = vec4(finalColor, alpha);
                }
            `
        };

        const createParticleSystem = (count, size, color, spread, baseSpeed) => {
            const geometry = new THREE.BufferGeometry();
            const positions = new Float32Array(count * 3);
            const colors = new Float32Array(count * 3);
            const sizes = new Float32Array(count);
            const opacities = new Float32Array(count);
            const phases = new Float32Array(count);
            const velocities = new Float32Array(count * 3);

            const material = new THREE.ShaderMaterial({
                uniforms: {
                    time: { value: 0 }
                },
                vertexShader: particleVertexShader,
                fragmentShader: particleFragmentShader,
                transparent: true,
                depthWrite: false,
                blending: THREE.AdditiveBlending
            });

            const colorObj = new THREE.Color(color);
            for (let i = 0; i < count; i++) {
                const i3 = i * 3;
                const angle = Math.random() * Math.PI * 2;
                const r = Math.random() * spread;
                const phi = Math.random() * Math.PI * 2;
                const theta = Math.random() * Math.PI;

                positions[i3] = Math.sin(theta) * Math.cos(phi) * r;
                positions[i3 + 1] = Math.sin(theta) * Math.sin(phi) * r;
                positions[i3 + 2] = Math.cos(theta) * r;

                const speed = baseSpeed * (0.5 + Math.random());
                velocities[i3] = Math.sin(angle) * speed;
                velocities[i3 + 1] = Math.cos(angle) * speed;
                velocities[i3 + 2] = (Math.random() - 0.5) * speed;

                colors[i3] = colorObj.r;
                colors[i3 + 1] = colorObj.g;
                colors[i3 + 2] = colorObj.b;

                sizes[i] = size * (0.8 + Math.random() * 0.4);
                opacities[i] = 0.6 + Math.random() * 0.4;
                phases[i] = Math.random() * Math.PI * 2;
            }

            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geometry.setAttribute('customColor', new THREE.BufferAttribute(colors, 3));
            geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
            geometry.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));
            geometry.setAttribute('phase', new THREE.BufferAttribute(phases, 1));
            geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3));

            const points = new THREE.Points(geometry, material);
            const entity = document.createElement('a-entity');
            entity.setObject3D('particle-system', points);
            entity.setAttribute('position', `${x} ${y} ${z}`);

            return { entity, geometry, material, points };
        };

        const mainRing = createParticleSystem(800 * intensity, 0.15, '#ffffff', radius * 1.2, 2);
        const energyTrails = createParticleSystem(600 * intensity, 0.1, '#9933ff', radius * 1.5, 1.5);
        const centerPortal = createParticleSystem(400 * intensity, 0.08, '#4411aa', radius * 0.8, 1);

        const cyberGeometry = new THREE.PlaneGeometry(radius * 2, radius * 2, 128, 128);
        const cyberMaterial = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                portalColor: { value: new THREE.Color('#4411aa') }
            },
            vertexShader: cyberPortalShader.vertexShader,
            fragmentShader: cyberPortalShader.fragmentShader,
            transparent: true,
            side: THREE.DoubleSide
        });

        const cyberMesh = new THREE.Mesh(cyberGeometry, cyberMaterial);
        const cyberEntity = document.createElement('a-entity');
        cyberEntity.setObject3D('mesh', cyberMesh);
        cyberEntity.setAttribute('position', `${x} ${y} ${z-0.1}`);

        const clock = new THREE.Clock();
        const animate = () => {
            const time = clock.getElapsedTime();

            [mainRing, energyTrails, centerPortal].forEach(system => {
                system.material.uniforms.time.value = time;

                const positions = system.geometry.attributes.position.array;
                const velocities = system.geometry.attributes.velocity.array;
                const opacities = system.geometry.attributes.opacity.array;
                const phases = system.geometry.attributes.phase.array;

                for (let i = 0; i < positions.length; i += 3) {
                    positions[i] += velocities[i] * 0.016;
                    positions[i + 1] += velocities[i + 1] * 0.016;
                    positions[i + 2] += velocities[i + 2] * 0.016;

                    const dist = Math.sqrt(
                        positions[i] * positions[i] +
                        positions[i + 1] * positions[i + 1] +
                        positions[i + 2] * positions[i + 2]
                    );

                    if (dist > radius * 1.5) {
                        const scale = (radius * 1.2) / dist;
                        positions[i] *= scale;
                        positions[i + 1] *= scale;
                        positions[i + 2] *= scale;
                        velocities[i] *= -0.5;
                        velocities[i + 1] *= -0.5;
                        velocities[i + 2] *= -0.5;
                    }

                    opacities[i/3] *= 0.99;
                    if (opacities[i/3] < 0.1) {
                        opacities[i/3] = 1.0;
                        phases[i/3] = Math.random() * Math.PI * 2;
                    }
                }

                system.geometry.attributes.position.needsUpdate = true;
                system.geometry.attributes.opacity.needsUpdate = true;
                system.geometry.attributes.phase.needsUpdate = true;
            });

            cyberMaterial.uniforms.time.value = time;

            requestAnimationFrame(animate);
        };

        this.scene.appendChild(mainRing.entity);
        this.scene.appendChild(energyTrails.entity);
        this.scene.appendChild(centerPortal.entity);
        this.scene.appendChild(cyberEntity);

        animate();

        const mainId = this.generateId('portal-main');
        const energyId = this.generateId('portal-energy');
        const centerId = this.generateId('portal-center');
        const cyberId = this.generateId('portal-cyber');

        this.elements.set(mainId, mainRing.entity);
        this.elements.set(energyId, energyTrails.entity);
        this.elements.set(centerId, centerPortal.entity);
        this.elements.set(cyberId, cyberEntity);

        return [mainId, energyId, centerId, cyberId];
    }

    updatePortalColors(ids, primaryColor = '#4411aa', secondaryColor = '#9933ff') {
        const primary = new THREE.Color(primaryColor);
        const secondary = new THREE.Color(secondaryColor);

        ids.forEach(id => {
            const entity = this.elements.get(id);
            if (entity && entity.object3D) {
                const obj = entity.object3D;
                if (obj.material && obj.material.uniforms) {
                    obj.material.uniforms.color.value.copy(primary);
                }
            }
        });
    }

    setPortalIntensity(ids, intensity = 1.0) {
        ids.forEach(id => {
            const entity = this.elements.get(id);
            if (entity && entity.object3D) {
                const obj = entity.object3D;
                if (obj.material && obj.material.uniforms) {
                    obj.material.uniforms.intensity.value = intensity;
                }
            }
        });
    }

    // Alternative simplified version without distance scaling
    addSimpleLensFlare(x = 0, y = 0, z = 0, color = '#ffffff', size = 100) {
        const id = this.generateId('simple-lensflare');
        const lensflare = document.createElement('a-plane');
        
        lensflare.setAttribute('position', `${x} ${y} ${z}`);
        lensflare.setAttribute('width', size);
        lensflare.setAttribute('height', size);
        lensflare.setAttribute('material', {
            src: '../../img/lens.jpg',
            transparent: true,
            color: color,
            opacity: 0.7,
            shader: 'flat',
            blending: 'additive'
        });
        lensflare.setAttribute('look-at', '[camera]');
        
        lensflare.id = id;
        this.scene.appendChild(lensflare);
        this.elements.set(id, lensflare);
        
        return id;
    }

    addFogEffect(color = '#000000', near = 1, far = 1000) {
        this.scene.setAttribute('fog', {
            type: 'linear',
            color: color,
            near: near,
            far: far
        });
        return this;
    }

    addExponentialFog(color = '#000000', density = 0.1) {
        this.scene.setAttribute('fog', {
            type: 'exponential',
            color: color,
            density: density
        });
        return this;
    }

    addSkyGradient(colorTop = '#1c1c1c', colorBottom = '#727272') {
        const id = this.generateId('sky');
        const sky = document.createElement('a-sky');
        sky.setAttribute('material', {
            shader: 'gradientsky',
            topColor: colorTop,
            bottomColor: colorBottom
        });
        sky.id = id;
        this.scene.appendChild(sky);
        this.elements.set(id, sky);
        return id;
    }
}

export default AFrameObjectBuilder;